'use client';

import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldAlert } from 'lucide-react';

import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from '@/components/ui/empty';
import { usePerm, type PermissionKey } from '@/hooks/use-perm';

interface PermissionDeniedProps {
    permission: PermissionKey;
}

export function PermissionDenied({ permission }: PermissionDeniedProps) {
    const { t } = useTranslation('common');

    return (
        <section className="mx-auto flex max-w-2xl items-center justify-center px-4 py-16">
            <Empty>
                <EmptyHeader>
                    <EmptyMedia variant="icon">
                        <ShieldAlert className="h-6 w-6" />
                    </EmptyMedia>
                    <EmptyTitle>{t('permissionDenied.title')}</EmptyTitle>
                    <EmptyDescription>{t('permissionDenied.description')}</EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                    <p className="text-muted-foreground font-mono text-xs">
                        {t('permissionDenied.requiredPermission', { permission })}
                    </p>
                </EmptyContent>
            </Empty>
        </section>
    );
}

function PermissionLoading() {
    return (
        <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
    );
}

export function withRequiredPermission<P extends object>(
    Component: ComponentType<P>,
    permission: PermissionKey,
): ComponentType<P> {
    function Guarded(props: P) {
        const { can, isLoaded } = usePerm();

        if (!isLoaded) {
            return <PermissionLoading />;
        }

        if (!can(permission)) {
            return <PermissionDenied permission={permission} />;
        }

        return <Component {...props} />;
    }

    const name = Component.displayName ?? Component.name ?? 'Component';
    Guarded.displayName = `withRequiredPermission(${name})`;

    return Guarded;
}
